import type { PixelClient } from "../client.js";
import { isGalleryClick } from "./gallery.js";
import { pageContext } from "./page-context.js";

const MAX_LABEL_LEN = 80;
const DEDUP_MS = 2_000;

/** Track every user click anywhere on the page (including body and non-interactive elements). */
export function attachClickTracking(client: PixelClient, productId?: string): () => void {
  const recent = new Map<string, number>();

  const onClick = (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target || target === document.documentElement) return;
    if (isGalleryClick(target)) return;

    const key = clickKey(target);
    const now = Date.now();
    const prev = recent.get(key);
    if (prev !== undefined && now - prev < DEDUP_MS) return;
    recent.set(key, now);

    const props: Record<string, unknown> = {
      element: target.tagName.toLowerCase(),
      ...(productId ? { product_id: productId } : {}),
    };

    // Body/html carry the whole page text — skip label to avoid noise.
    if (target !== document.body) {
      const label = (target.textContent ?? "").trim().replace(/\s+/g, " ").slice(0, MAX_LABEL_LEN);
      if (label) props.label = label;
    }

    if (target instanceof HTMLImageElement) {
      const src = target.currentSrc || target.src;
      if (src) props.image_src = src.slice(0, 200);
    }

    if (target instanceof HTMLAnchorElement && target.href) {
      try {
        const u = new URL(target.href);
        props.href_path = u.pathname;
      } catch {
        // ignore bad href
      }
    }

    const cls =
      typeof target.className === "string" && target.className
        ? target.className.trim().split(/\s+/).slice(0, 3).join(" ")
        : "";
    if (cls) props.class = cls;

    const section = target.closest("[data-product-section]")?.getAttribute("data-product-section");
    if (section) props.section = section;

    void client.track("click", props, pageContext());
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

function clickKey(el: HTMLElement): string {
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className
      ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${el.tagName}${id}${cls}`;
}
